import { DEFAULT_PRESET_ID, THEME_PRESETS } from "@app/core";
import { Button, Card, ChevronDown, ChevronUp } from "@app/ui";
import { useEffect, useState } from "react";

import { loadPresetConfirmed } from "../../../features/theme/theme-metadata.js";
import { useI18n } from "../../../lib/useI18n.js";
import { useThemeStore } from "../../../stores/theme.store.js";
import { SettingsAnchor } from "../SettingsCard.js";
import { ThemePresetPicker } from "./ThemePresetPicker.js";

/** The preset picker, collapsible: expanded only for a pristine install that
 *  never picked a theme — once the user has chosen one (and kept it), visits
 *  start collapsed to a SINGLE line: title, active name + palette strip.
 *  Shared by the Theme and Organize pages (the preset chooser must accompany
 *  the palette board wherever it appears). */
export function PresetPickerCard({ anchorId = "theme-preset" }: { anchorId?: string } = {}) {
  const { t } = useI18n();
  const config = useThemeStore((s) => s.config);
  const revertAll = useThemeStore((s) => s.revertAll);
  // null = not decided yet; render collapsed meanwhile (most users have picked).
  const [open, setOpen] = useState<boolean | null>(null);
  useEffect(() => {
    let alive = true;
    const pristine = useThemeStore.getState().config.preset === DEFAULT_PRESET_ID;
    void loadPresetConfirmed().then((confirmed) => {
      if (alive) setOpen(!confirmed && pristine);
    });
    return () => { alive = false; };
  }, []);

  const active = THEME_PRESETS.find((p) => p.id === config.preset);
  const name = active?.name ?? t("theme.custom");
  const strip = (active?.entityPalette ?? config.entityPalette).slice(0, 10);

  return (
    <SettingsAnchor id={anchorId}>
      <Card className="p-3">
        <div className="flex items-center gap-3">
          <h3 className="shrink-0 font-medium">{t("theme.preset")}</h3>
          {!open && (
            <>
              <span className="truncate text-sm text-muted-foreground">{name}</span>
              <span className="flex h-4 w-40 shrink-0 overflow-hidden rounded">
                {strip.map((hex, i) => <span key={i} className="h-full flex-1" style={{ background: hex }} />)}
              </span>
            </>
          )}
          <span className="flex-1" />
          {open && (
            <Button variant="ghost" size="sm" onClick={() => revertAll()}>{t("common.reset")}</Button>
          )}
          <Button variant="ghost" size="sm" onClick={() => setOpen(!open)} aria-expanded={!!open}>
            {open ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            <span className="ml-1">{open ? t("theme.preset.hide") : t("theme.preset.change")}</span>
          </Button>
        </div>
        {open && (
          <div className="mt-3">
            <p className="mb-3 text-sm text-muted-foreground">{t("theme.preset.description")}</p>
            <ThemePresetPicker />
          </div>
        )}
      </Card>
    </SettingsAnchor>
  );
}
